import type { Difficulty, LevelDefinition } from "@/domain";

import { createFocusSocialLevel } from "./create-level";

export const FRIENDSHIP_TOPICS = [
  "sharing",
  "boundaries",
  "emotions",
  "conflict-repair",
  "inclusion",
  "help-seeking"
] as const;

export type FriendshipTopic = (typeof FRIENDSHIP_TOPICS)[number];

export interface FriendshipChoiceDefinition {
  id: string;
  label: string;
  audio: string;
  feedback: string;
  feedbackAudio: string;
}

export interface FriendshipStoriesPayload {
  topic: FriendshipTopic;
  sceneNarration: string;
  question: string;
  narrationAudio: string;
  choices: FriendshipChoiceDefinition[];
  bestChoiceId: string;
  followUp?: string;
  followUpAudio?: string;
}

type FriendshipChoiceInput = Omit<
  FriendshipChoiceDefinition,
  "audio" | "feedbackAudio"
>;

function withFriendshipAudio(
  difficulty: Difficulty,
  payload: Omit<
    FriendshipStoriesPayload,
    "narrationAudio" | "choices" | "followUpAudio"
  > & {
    choices: FriendshipChoiceInput[];
  }
): FriendshipStoriesPayload {
  const audioPrefix =
    "/audio/focus-social/friendship-stories-" + String(difficulty) + "-";

  return {
    ...payload,
    narrationAudio: audioPrefix + "narration.ogg",
    choices: payload.choices.map((choice) => ({
      ...choice,
      audio: audioPrefix + "choice-" + choice.id + ".ogg",
      feedbackAudio: audioPrefix + "feedback-" + choice.id + ".ogg"
    })),
    ...(payload.followUp === undefined
      ? {}
      : { followUpAudio: audioPrefix + "follow-up.ogg" })
  };
}

export const friendshipStoriesLevels: LevelDefinition<FriendshipStoriesPayload>[] = [
  createFocusSocialLevel(
    "friendship-stories",
    {
      difficulty: 1,
      title: "一起用蜡笔",
      instruction: "小鹿正用着美术角唯一一盒蜡笔，琪琪也铺好了画纸。她可以怎样开口？",
      objectives: ["用友好的话提出共享请求", "理解询问和轮流都是尊重朋友的做法"]
    },
    withFriendshipAudio(1, {
      topic: "sharing",
      sceneNarration: "美术角只有一盒蜡笔。小鹿正涂着颜色，琪琪抱着画纸走过来，也想画一幅画。",
      question: "琪琪怎样说，能和小鹿一起想办法？",
      choices: [
        {
          id: "ask-to-share",
          label: "问小鹿：我们可以一起用吗？",
          feedback: "琪琪先问一声，把自己的想法说清楚。这样能给小鹿留出回答的时间。"
        },
        {
          id: "wait-for-turn",
          label: "先在旁边等小鹿用完",
          feedback: "等一等很温柔。琪琪也可以问问什么时候轮到自己，心里就不用一直猜。"
        },
        {
          id: "take-one-now",
          label: "直接从盒子里拿一支",
          feedback: "琪琪一定很想马上画。先问一声，再商量怎么轮流，蜡笔用起来会更开心。"
        }
      ],
      bestChoiceId: "ask-to-share"
    })
  ),
  createFocusSocialLevel(
    "friendship-stories",
    {
      difficulty: 2,
      title: "我想留一点空间",
      instruction: "明明伸手想拉安安，安安今天更想自己排队。她可以怎样把感受说清楚？",
      objectives: ["清楚而友好地表达身体边界", "尊重自己和别人的不同感受"]
    },
    withFriendshipAudio(2, {
      topic: "boundaries",
      sceneNarration: "大家准备排队。明明伸手想拉安安，安安今天不想被拉手，更想自己站好。",
      question: "安安可以怎样说，让明明听懂她的边界？",
      choices: [
        {
          id: "say-need-space",
          label: "说：我今天不想拉手，请给我一点空间",
          feedback: "这句话温柔又清楚。明明知道要停下来，也知道安安需要一点舒服的空间。"
        },
        {
          id: "step-away-silently",
          label: "悄悄往旁边退，不说原因",
          feedback: "先退开，让自己舒服一点没问题。再说一句“我想自己站”，明明会更容易听懂。"
        },
        {
          id: "hold-hands-anyway",
          label: "虽然不舒服，还是勉强拉手",
          feedback: "不舒服时，可以认真说“不”。告诉朋友你想怎样排队，也是在照顾自己。"
        }
      ],
      bestChoiceId: "say-need-space",
      followUp: "朋友说不想被碰时，停下手，留出一点空间。这就是尊重。"
    })
  ),
  createFocusSocialLevel(
    "friendship-stories",
    {
      difficulty: 3,
      title: "紧张也可以说出来",
      instruction: "新游戏那边笑声不断，乐乐想加入，心里却有点紧张。什么办法能帮他开口加入？",
      objectives: ["辨认并说出紧张的感受", "用简单方法调节情绪并尝试交流"]
    },
    withFriendshipAudio(3, {
      topic: "emotions",
      sceneNarration: "几个小朋友正在玩新的合作游戏。乐乐走近一点，心跳有点快，不知道第一句话怎么说。",
      question: "乐乐可以先做什么，让心里松一松？",
      choices: [
        {
          id: "name-feeling-and-ask",
          label: "慢慢呼吸，说：我有点紧张，可以一起玩吗？",
          feedback: "慢慢呼吸，再把紧张说出来。朋友听懂了，就知道可以邀请乐乐一起玩。"
        },
        {
          id: "watch-without-speaking",
          label: "一直站在旁边看，不告诉别人",
          feedback: "先看看玩法也可以。准备好以后，找一位朋友问问：“我可以一起玩吗？”"
        },
        {
          id: "leave-because-nervous",
          label: "觉得紧张，就马上离开",
          feedback: "想离开一会儿也没关系。等心里准备好了，可以请朋友或老师陪你再走近一点。"
        }
      ],
      bestChoiceId: "name-feeling-and-ask",
      followUp: "紧张、害羞、兴奋都可以说出来。别人听懂你的感受，就更知道怎样陪你。"
    })
  ),
  createFocusSocialLevel(
    "friendship-stories",
    {
      difficulty: 4,
      title: "积木倒了以后",
      instruction: "哗啦，东东转身时碰倒了米米搭了很久的积木塔。接下来怎样做，能把小冲突慢慢修好？",
      objectives: ["在意外冲突后承认发生的事情", "用道歉和实际行动修补关系"]
    },
    withFriendshipAudio(4, {
      topic: "conflict-repair",
      sceneNarration: "哗啦一声，东东转身时碰倒了米米搭了很久的积木塔。米米看着散开的积木，很失望。",
      question: "东东先说什么、做什么，能让米米感觉被在意？",
      choices: [
        {
          id: "acknowledge-and-repair",
          label: "说：对不起，我碰倒了。我们一起重新搭好吗？",
          feedback: "东东说清楚了发生的事，也愿意陪米米重搭。这样能让米米知道，他在认真补救。"
        },
        {
          id: "rebuild-without-speaking",
          label: "不说话，马上自己重新搭",
          feedback: "愿意重搭，是个好开始。先听听米米想不想一起搭，再动手会更贴心。"
        },
        {
          id: "blame-the-table",
          label: "说是桌子太挤，和自己没关系",
          feedback: "不是故意的，也可以诚实承认。说清楚“是我碰倒的”，再问米米需要什么。"
        }
      ],
      bestChoiceId: "acknowledge-and-repair",
      followUp: "碰坏了朋友的东西，可以先说清楚，再听听朋友的想法，最后一起商量怎么办。"
    })
  ),
  createFocusSocialLevel(
    "friendship-stories",
    {
      difficulty: 5,
      title: "给新朋友留个位置",
      instruction: "接力游戏要开始了，新同学小雨站在旁边认真看。大家怎么邀请，让她自己选要不要玩？",
      objectives: ["主动留意还没有加入集体的同伴", "用邀请和规则说明帮助新朋友参与"]
    },
    withFriendshipAudio(5, {
      topic: "inclusion",
      sceneNarration: "几个小朋友正排好接力队伍。新同学小雨站在旁边，认真看着大家。",
      question: "大家可以怎样给小雨一个舒服的邀请？",
      choices: [
        {
          id: "invite-and-explain",
          label: "邀请小雨，并简单讲一讲游戏规则",
          feedback: "先邀请，再把规则讲清楚。小雨听明白后，可以安心决定要不要参加。"
        },
        {
          id: "assume-not-interested",
          label: "觉得小雨没有开口，应该不想玩",
          feedback: "站在旁边看，不一定是不想玩。可以问一句“要一起吗？”，再听小雨怎么说。"
        },
        {
          id: "keep-playing",
          label: "先开始游戏，等小雨自己走过来",
          feedback: "有时，一句邀请就能让人迈出第一步。给小雨留个位置，再问问她吧。"
        }
      ],
      bestChoiceId: "invite-and-explain",
      followUp: "邀请说出口后，也要听对方的回答。想现在参加、晚一点参加，或不参加，都可以。"
    })
  ),
  createFocusSocialLevel(
    "friendship-stories",
    {
      difficulty: 6,
      title: "需要帮助就告诉老师",
      instruction: "放学集合时，小宁没看见来接的大人，班主任就在身边。她该怎么做，才能安心等到家人？",
      objectives: ["遇到不确定的情况时向可信任的成人求助", "在安全地点清楚说明自己的需要"]
    },
    withFriendshipAudio(6, {
      topic: "help-seeking",
      sceneNarration: "放学集合时，小宁没有看到来接的大人。班主任就在集合点，正照看着大家。",
      question: "小宁怎样做，既安全又能马上得到帮助？",
      choices: [
        {
          id: "tell-teacher-and-wait",
          label: "告诉班主任，并在集合点一起等",
          feedback: "小宁把情况告诉班主任，又留在约定的集合点。老师知道了，就能陪她联系家人。"
        },
        {
          id: "search-alone",
          label: "自己到校园各处寻找",
          feedback: "想快点找到家人很自然。先别一个人离开集合点，把情况告诉班主任，让老师来联系。"
        },
        {
          id: "wait-without-telling",
          label: "什么也不说，一个人安静地等",
          feedback: "留在集合点是对的。再把情况告诉班主任，老师就能马上帮你。"
        }
      ],
      bestChoiceId: "tell-teacher-and-wait",
      followUp: "需要帮助时，可以告诉大人：发生了什么、你在哪里、希望得到什么帮助。"
    })
  )
];
